import { Table } from '@dixa/lib-shared-habla';
import { QueueMetrics, QueueMetricType } from 'lib-dashboard-types';
import { Fragment, memo, useMemo } from 'react';

import { useQueueDashboardMetricsMap } from 'utils/dashboard';

import { useQueueDashboardQueueContext } from '../queue/queueDashboardQueue.context';

import { QueueDashboardAgentPresenceMetric } from './components/agentPresence/agentPresenceMetric';
import { QueueDashboardDailyConversationsMetric } from './components/dailyConversations/dailyConversationsMetric';
import { QueueDashboardPendingConversationsMetric } from './components/pendingConversations/pendingConversationsMetric';
import { QueueDashboardQueuedTimeMetric } from './components/queuedTime/queuedTimeMetric';
import { QueueDashboardResponseTimeMetric } from './components/responseTime/responseTimeMetric';
import styles from './queueDashboardMetrics.module.scss';

type QueueDashboardMetricsProps = {
  /** The metrics data for the current dashboard queue. */
  queueMetrics?: QueueMetrics;
};

export const QueueDashboardMetrics = memo(function QueueDashboardMetrics({
  queueMetrics,
}: QueueDashboardMetricsProps): JSX.Element {
  const { queue, layout } = useQueueDashboardQueueContext();

  const { metrics, sla_policies } = queueMetrics ?? {};

  const [_, { get }] = useQueueDashboardMetricsMap({
    queueId: queue?.id,
    metrics,
  });

  const responseTimeSlaPolicy = useMemo(
    () =>
      sla_policies?.find((policy) =>
        policy.metrics.some(({ metric }) => metric === 'response_time'),
      ),
    [sla_policies],
  );

  const metricElements = useMemo(
    (): Record<QueueMetricType, JSX.Element> => ({
      agent_presence: (
        <QueueDashboardAgentPresenceMetric
          className={styles.metric}
          metric={get('agent_presence')}
        />
      ),
      pending_conversations: (
        <QueueDashboardPendingConversationsMetric
          className={styles.metric}
          metric={get('pending_conversations')}
        />
      ),
      queued_time: (
        <QueueDashboardQueuedTimeMetric
          className={styles.metric}
          metric={get('queued_time')}
        />
      ),
      daily_conversations: (
        <QueueDashboardDailyConversationsMetric
          className={styles.metric}
          metric={get('daily_conversations')}
        />
      ),
      response_time: (
        <QueueDashboardResponseTimeMetric
          className={styles.metric}
          metric={get('response_time')}
          slaPolicy={responseTimeSlaPolicy}
        />
      ),
    }),
    [get, responseTimeSlaPolicy],
  );

  if (layout === 'compact') {
    return (
      <>
        {Object.entries(metricElements).map(([type, element]) => (
          <Table.Td key={type}>{element}</Table.Td>
        ))}
      </>
    );
  }

  return (
    <div className={styles.metrics}>
      {Object.entries(metricElements).map(([type, element]) => (
        <Fragment key={type}>{element}</Fragment>
      ))}
    </div>
  );
});
